import React from 'react';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import { LzTRouteEntries } from '@lz/services/Routes';
import { LzTNavItems } from '@lz/components/layout/Navigation/types';
import { lzFKebabToSnakeCase, lzFKebabToWordCase } from '@lz/services/String';
import { LzTModuleChildVariation } from './types';
import LzCEntity from './Entity';

export interface LzIModuleChildBase {
    /**
     * Interface variation definition.
     */
    variation?: LzTModuleChildVariation;

    /**
     * Unique kebab-case identity.
     */
    ident: string;

    /**
     * Name of display purposes (optional: will be evaluated from ident).
     */
    name?: string;

    /**
     * Icon for navigation (optional: will be set to a default icon).
     */
    icon?: React.ReactNode;

    /**
     * Base route with leading slash, excluding the current route part (e.g. /system, /params/accounting, etc.).
     */
    baseRoute?: string;

    /**
     * Slug in kebab-case for routing purposes (optional: equals to ident if not provided).
     */
    slug?: string;

    /**
     * Full route path if user wants to define (optional: will be evaluated if set to empty).
     */
    fullRoute?: string;

    /**
     * Get full route path if user wants to define dynamically (optional: will be evaluated if not defined)
     */
    getFullRoute?: () => string;

    /**
     * Base rights path separated by dots(.) and in snake_case, excluding the current entity (e.g. system, params.accounting, etc.).
     */
    baseRightsPath?: string;

    /**
     * Rights key for current enty in snake_case (optional: will be evaluated from ident).
     */
    rightsKey?: string;

    /**
     * All the routes for this child (optional).
     */
    routes?: LzTRouteEntries;

    /**
     * All the navigation items for this child (optional).
     */
    navItems?: LzTNavItems;
}

class LzCModuleChildBase implements LzIModuleChildBase {
    variation: LzTModuleChildVariation;
    ident: string;
    name: string;
    icon: React.ReactNode;
    baseRoute: string;
    slug: string;
    baseRightsPath: string;
    rightsKey: string;
    routes: LzTRouteEntries;
    navItems: LzTNavItems;
    _fullRoute: string;
    getFullRoute?: (() => string) | undefined;

    constructor(model?: LzIModuleChildBase) {
        this.variation = 'Module'; // by default make it a Module
        this.ident = model?.ident || '';
        this.name = model?.name || lzFKebabToWordCase(this.ident);
        this.icon = model?.icon || <DoubleArrowIcon />;
        this.baseRoute = model?.baseRoute || '';
        this.slug = model?.slug || this.ident;
        this.baseRightsPath = model?.baseRightsPath || '';
        this.rightsKey = typeof model?.rightsKey === 'undefined' ? lzFKebabToSnakeCase(this.ident) : model?.rightsKey;
        this.routes = model?.routes || [];
        this.navItems = model?.navItems || [];

        this._fullRoute = model?.fullRoute || '';
        this.getFullRoute = model?.getFullRoute;
    }

    get rightsPath(): string {
        return ('' === this.baseRightsPath || '' === this.rightsKey ? '' : this.baseRightsPath + '.') + this.rightsKey;
    }

    set fullRoute(value: string) {
        this._fullRoute = value;
    }

    get fullRoute(): string {
        if (typeof this.getFullRoute !== 'undefined') {
            return this.getFullRoute();
        }

        return this._fullRoute !== '' ? this._fullRoute : this.baseRoute + '/' + this.slug;
    }

    /**
     * Placeholder function to get a child entity based on the given ident.
     *
     * @param ident The string ident value.
     *
     * @returns Returns either null or the found entity.
     */
    getChildEntity(ident: string): LzCEntity | null {
        // If not found then return null
        return null;
    }
}

export default LzCModuleChildBase;
